import { createRef, forwardRef, useMemo, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { IMaskInput } from 'react-imask';
import styles from './Input.module.css';

const InputDefault = ({ ...props }) => (
  <input {...props}  />
);

function InputView(props, ref) {
  const {
    className = '',
    isFocused,
    onFocus,
    onBlur,
    value,
    onChange,
    onReset,
    tabIndex,
    label,
    placeholder,
    icon,
    withoutBG,
    error,
    errorText,
    onClick,
    dataTestid,
    disabled,
    readOnly,
    size = 'lg',
    theme = 'filled',
    autoFocus,
    mask,
  } = props;
  const containerRef = createRef(null);
  const t = useTranslations();
  const inputId = useMemo(() => {
    return `input-id-${label || placeholder}`;
  }, [label, placeholder]);
  let sizeStyles;
  switch (size) {
    case 'md':
      sizeStyles = styles.sizeMedium;
      break;
    case 'lg':
    default:
      sizeStyles = styles.sizeLarge;
      break;
  }
  let themeStyles;
  switch (theme) {
    case 'filled':
      themeStyles = styles.box_filled;
      break;
    case 'outlined':
    default:
      themeStyles = styles.box_outlined;
      break;
  }

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      containerRef.current.querySelector('input').focus();
    }
  }, [containerRef.current]);

  const InputComponent = mask ? IMaskInput : InputDefault;

  return (
    <div
      className={`
      ${styles.box}
      ${sizeStyles}
      ${themeStyles}
      ${error ? styles.box_error : ''}
      ${isFocused ? styles.box_focused : ''}
      ${className}
      ${withoutBG ? '!h-14' : ''}
      ${withoutBG ? styles.without_bg : ''}
      ${disabled ? styles.box_disabled : ''}
      `}
      onClick={disabled ? undefined : onClick}
      data-guestit-error={error}
      data-testid={dataTestid}
    >
      <div className={styles.input}>
        <span
          className={`
          icon-input inline-flex relative top-[-3px]
          ${styles.icon}
          ${error ? '!text-rose-400' : ''}
          ${isFocused ? '!text-teal-600' : ''}
          `}
        >
          <i className={`text-[24px] h-6 ${icon}`} />
        </span>

        <div ref={containerRef} className="flex flex-col gap-2 w-full relative">
          <label
            className="text-xs text-slate-600 truncate inline-block h-[18px] mt-[-4px] mb-[-4px]"
            htmlFor={inputId}
          >
            <span>{t(label)}</span>
          </label>
          <InputComponent
            mask={mask ? "+00 00 000 00 00" : null}
            className={`
            inline-flex h-[19px] w-full bg-transparent
            font-medium text-black truncate max-sm:text-base leading-[10px]
            focus:outline-none placeholder:text-slate-400
            pr-6 mt-[-4px] mb-[-5px]
            `}
            onFocus={onFocus}
            onBlur={onBlur}
            value={value}
            placeholder={t(placeholder)}
            onChange={onChange}
            ref={ref}
            tabIndex={tabIndex}
            disabled={disabled}
            readOnly={readOnly}
            id={inputId}
          />
          {error && (
            <i className="icon-error absolute right-0 top-0.5 leading-6 text-[24px] text-rose-400" />
          )}
        </div>
        {errorText && (
          <div className="absolute -bottom-6 text-rose-400 text-xs font-normal leading-[18px] truncate max-sm:inset-x-0">
            {t(errorText)}
          </div>
        )}
      </div>

      {Boolean(value && onReset && !disabled) && (
        <button
          className="bg-transparent self-center outline-slate-300 leading-none"
          type="button"
          onClick={onReset}
          tabIndex={tabIndex}
        >
          <i className="icon-close_round text-[24px]" />
        </button>
      )}
    </div>
  );
}

export const Input = forwardRef(InputView);
